import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, BookOpen, Eraser, Printer, Save, Scale } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { formatBDT } from "@/lib/bdCurrency";
import { toBengaliNumerals, toEnglishNumerals } from "@/lib/bdNumerals";
import { GRAMS_PER_VORI, voriToGrams } from "@/lib/units/vori";
import { MADHHABS, getMadhhab, type MadhhabId } from "@/data/madhhabs";
import { DEFAULT_PRICES, LAST_UPDATED, SOURCE } from "@/data/bdGoldPrices";
import {
  calculateZakat,
  EMPTY_ASSETS,
  EMPTY_LIABILITIES,
  NISAB_GOLD_G,
  NISAB_SILVER_G,
  type AssetInputs,
  type LiabilityInputs,
  type NisabBasis,
  type PriceInputs,
  type YearType,
  type ZakatResult,
} from "@/lib/calculators/zakat";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Zakat Calculator (Bangladesh)", bn: "যাকাত ক্যালকুলেটর (বাংলাদেশ)" },
  desc: {
    en: "Calculate your annual Zakat in BDT — gold (vori/gram), silver, cash, DPS, FDR, Sanchaypatra, bKash, Nagad, business assets and more. Hanafi default with multi-madhhab support.",
    bn: "বাংলাদেশের জন্য বার্ষিক যাকাত হিসাব করুন — সোনা (ভরি/গ্রাম), রুপা, নগদ, DPS, FDR, সঞ্চয়পত্র, বিকাশ, নগদ, ব্যবসায়িক সম্পদ। হানাফী মাযহাব ডিফল্ট।",
  },
  madhhab: { en: "Madhhab", bn: "মাযহাব" },
  nisabMethod: { en: "Nisab method", bn: "নিসাবের ভিত্তি" },
  silverNisab: { en: "Silver (612.36 g) — recommended", bn: "রুপা (৬১২.৩৬ গ্রাম) — উত্তম" },
  goldNisab: { en: "Gold (87.48 g)", bn: "সোনা (৮৭.৪৮ গ্রাম)" },
  yearType: { en: "Hawl basis", bn: "হাওয়াল ভিত্তি" },
  lunar: { en: "Lunar (354 days, 2.5%)", bn: "চাঁদ্র (৩৫৪ দিন, ২.৫%)" },
  gregorian: { en: "Gregorian (365.25 d, 2.5775%)", bn: "গ্রেগরিয়ান (৩৬৫.২৫ দিন, ২.৫৭৭৫%)" },
  prices: { en: "Reference prices (BDT per vori)", bn: "মূল্য তথ্য (টাকা/ভরি)" },
  pricesNote: {
    en: "Verify current prices with BAJUS or your local jeweler before paying Zakat.",
    bn: "যাকাত পরিশোধের আগে BAJUS বা স্থানীয় জুয়েলার্স থেকে বর্তমান দাম যাচাই করুন।",
  },
  assets: { en: "Assets", bn: "সম্পদ" },
  liabilities: { en: "Liabilities", bn: "দায়" },
  gold: { en: "Gold (vori)", bn: "সোনা (ভরি)" },
  silver: { en: "Silver (vori)", bn: "রুপা (ভরি)" },
  cashBank: { en: "Cash & Bank (BDT)", bn: "নগদ ও ব্যাংক (টাকা)" },
  wallets: { en: "Digital Wallets (BDT)", bn: "ডিজিটাল ওয়ালেট (টাকা)" },
  investments: { en: "Investments (BDT)", bn: "বিনিয়োগ (টাকা)" },
  business: { en: "Business Assets (BDT)", bn: "ব্যবসায়িক সম্পদ (টাকা)" },
  property: { en: "Commercial Property (BDT)", bn: "বাণিজ্যিক সম্পত্তি (টাকা)" },
  status: { en: "Nisab status", bn: "নিসাব স্ট্যাটাস" },
  exceeded: { en: "EXCEEDED — Zakat is obligatory", bn: "অতিক্রম করেছে — যাকাত ফরয" },
  belowNisab: { en: "Below Nisab — Zakat not obligatory this year", bn: "নিসাবের নিচে — এই বছর যাকাত ফরয নয়" },
  zakatDue: { en: "Zakat Due", bn: "প্রদেয় যাকাত" },
  totalAssets: { en: "Total Assets", bn: "মোট সম্পদ" },
  totalLiabilities: { en: "Total Liabilities", bn: "মোট দায়" },
  netWealth: { en: "Net Wealth", bn: "নেট সম্পদ" },
  nisab: { en: "Nisab Threshold", bn: "নিসাব সীমা" },
  reset: { en: "Reset", bn: "রিসেট" },
  save: { en: "Save", bn: "সংরক্ষণ" },
  print: { en: "Print", bn: "প্রিন্ট" },
  saved: { en: "Saved locally", bn: "স্থানীয়ভাবে সংরক্ষিত" },
  disclaimer: {
    en: "For educational and estimation purposes only. Consult a qualified scholar for complex cases. Zakat is due at the end of a full lunar year (Hawl) during which your wealth remained at or above Nisab.",
    bn: "এই হিসাব শুধুমাত্র শিক্ষামূলক ও আনুমানিক। জটিল ক্ষেত্রে যোগ্য আলেমের পরামর্শ নিন। যাকাত পূর্ণ এক চাঁদ্র বছর (হাওয়াল) যাবৎ সম্পদ নিসাবের সমান বা উপরে থাকলে ফরয হয়।",
  },
};

const RECIPIENTS: { en: string; bn: string; desc: Bilingual }[] = [
  { en: "Faqir", bn: "ফকীর", desc: { en: "The poor — those with little or no means.", bn: "দরিদ্র — যাদের সম্পদ অতি অল্প বা নেই।" } },
  { en: "Miskin", bn: "মিসকীন", desc: { en: "The needy — even more destitute than Faqir.", bn: "অভাবগ্রস্ত — ফকীরের চেয়েও বেশি অভাবী।" } },
  { en: "Amil", bn: "আমিল", desc: { en: "Zakat collectors and administrators.", bn: "যাকাত সংগ্রহকারী ও পরিচালক।" } },
  { en: "Mu'allafatul Qulub", bn: "মুআল্লাফাতুল কুলূব", desc: { en: "Those whose hearts are to be reconciled to Islam.", bn: "যাদের হৃদয় ইসলামের প্রতি আকৃষ্ট করা প্রয়োজন।" } },
  { en: "Riqab", bn: "রিকাব", desc: { en: "For freeing slaves (historical).", bn: "ক্রীতদাস মুক্তির জন্য (ঐতিহাসিক)।" } },
  { en: "Gharimin", bn: "গারিমীন", desc: { en: "The debt-burdened.", bn: "ঋণগ্রস্ত ব্যক্তি।" } },
  { en: "Fi Sabilillah", bn: "ফী সাবিলিল্লাহ", desc: { en: "In the path of Allah.", bn: "আল্লাহর পথে।" } },
  { en: "Ibnus Sabil", bn: "ইবনুস সবীল", desc: { en: "The traveler in need.", bn: "বিপদগ্রস্ত মুসাফির।" } },
];

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  { q: { en: "What percentage is Zakat?", bn: "যাকাত কত শতাংশ?" }, a: { en: "2.5% of net zakatable wealth on a lunar year, or about 2.5775% on a Gregorian year.", bn: "চাঁদ্র বছরে নেট যাকাতযোগ্য সম্পদের ২.৫%, গ্রেগরিয়ান বছরে প্রায় ২.৫৭৭৫%।" } },
  { q: { en: "How much Zakat on one vori of gold?", bn: "এক ভরি সোনায় কত যাকাত?" }, a: { en: "2.5% of its market value. At ৳1,72,000/vori (22K), Zakat ≈ ৳4,300 per vori.", bn: "তার বাজারমূল্যের ২.৫%। ২২ ক্যারেট ১,৭২,০০০ টাকা/ভরি হলে ≈ ৪,৩০০ টাকা।" } },
  { q: { en: "Is bKash / Nagad / Rocket money zakatable?", bn: "বিকাশ-নগদের টাকায় কি যাকাত?" }, a: { en: "Yes — mobile wallet balance is treated like cash and is fully zakatable.", bn: "হ্যাঁ — মোবাইল ওয়ালেটের ব্যালান্স নগদের মতো গণ্য, পুরোপুরি যাকাতযোগ্য।" } },
  { q: { en: "Do I pay Zakat on DPS?", bn: "DPS এ যাকাত দিতে হবে?" }, a: { en: "Yes. The cumulative balance you have deposited (and any profit) on your Zakat date is zakatable.", bn: "হ্যাঁ। যাকাতের তারিখে DPS এ জমা মূলধন ও মুনাফা যাকাতযোগ্য।" } },
  { q: { en: "Who pays Zakat on a wife's gold?", bn: "স্ত্রীর গহনায় যাকাত কে দেবে?" }, a: { en: "Hanafi: the owner — usually the wife. The husband may pay on her behalf with her permission.", bn: "হানাফী মতে: মালিক, অর্থাৎ সাধারণত স্ত্রী নিজেই। তবে স্বামী অনুমতি নিয়ে তার পক্ষ থেকে দিতে পারেন।" } },
  { q: { en: "If wealth equals Nisab exactly?", bn: "নিসাবের সমান হলে কি যাকাত?" }, a: { en: "Yes — Zakat becomes due once wealth reaches the Nisab threshold (and one lunar year passes).", bn: "হ্যাঁ — সম্পদ নিসাবের সমান হলেই যাকাত ওয়াজিব (এবং এক চাঁদ্র বছর অতিক্রান্ত হলে)।" } },
  { q: { en: "If I am in debt, do I still pay?", bn: "ঋণগ্রস্ত হলে যাকাত?" }, a: { en: "Subtract immediately due debts. If net wealth still meets Nisab, Zakat is due on the remainder.", bn: "যা দ্রুত পরিশোধ্য তা বিয়োগ করুন। নেট সম্পদ এখনো নিসাবের সমান হলে অবশিষ্টে যাকাত।" } },
  { q: { en: "What is Ushr on agricultural produce?", bn: "কৃষি পণ্যের উশর কত?" }, a: { en: "10% if rain-irrigated, 5% if irrigation cost was incurred. Calculated separately from Zakat.", bn: "বৃষ্টির পানিতে সেচ হলে ১০%, কৃত্রিম সেচে ৫%। যাকাত থেকে আলাদা হিসাব।" } },
  { q: { en: "Zakat on business inventory?", bn: "ব্যবসার পণ্যের যাকাত?" }, a: { en: "Hanafi: market value of stock-in-trade on your Zakat date is zakatable at 2.5%.", bn: "হানাফী মতে: যাকাতের তারিখে ব্যবসায়িক পণ্যের বাজারমূল্যের ২.৫%।" } },
  { q: { en: "Zakat on company shares?", bn: "কোম্পানির শেয়ারের যাকাত?" }, a: { en: "If held for resale: full market value. If held for dividends: zakatable portion of company assets (simplified: market value).", bn: "বিক্রির জন্য রাখলে: পূর্ণ বাজারমূল্য। লভ্যাংশের জন্য রাখলে: কোম্পানির যাকাতযোগ্য অংশ (সরল: বাজারমূল্য)।" } },
  { q: { en: "Zakat on FDR / Fixed Deposit?", bn: "FDR এ যাকাত?" }, a: { en: "Yes — the full FDR principal plus accrued profit on your Zakat date.", bn: "হ্যাঁ — যাকাতের তারিখে FDR এর মূলধন ও অর্জিত মুনাফা।" } },
  { q: { en: "Lunar vs Gregorian year for Zakat?", bn: "চাঁদ্র না গ্রেগরিয়ান বছর?" }, a: { en: "The Sunnah is the lunar year (Hawl). If you use Gregorian, scale the rate to ≈2.5775% to compensate for the 11 extra days.", bn: "সুন্নাহ অনুযায়ী চাঁদ্র বছর (হাওয়াল)। গ্রেগরিয়ান ব্যবহার করলে ১১ দিনের ব্যবধানের জন্য হার ≈২.৫৭৭৫% করা হয়।" } },
  { q: { en: "Is personal residence zakatable?", bn: "নিজের বাড়িতে যাকাত?" }, a: { en: "No. Personal residence, car and items in personal use are not zakatable.", bn: "না। নিজের বসবাসের বাড়ি, গাড়ি ও ব্যক্তিগত ব্যবহারের জিনিসে যাকাত নেই।" } },
  { q: { en: "Can I pay Zakat in Ramadan?", bn: "রমাদানে যাকাত দেওয়া যায়?" }, a: { en: "Yes — many prefer Ramadan for greater reward, but the obligation is at end of your personal Hawl.", bn: "হ্যাঁ — অনেকে অধিক সওয়াবের জন্য রমাদানে দেন, তবে ফরয হয় ব্যক্তিগত হাওয়ালের সমাপ্তিতে।" } },
  { q: { en: "Can I split Zakat among multiple recipients?", bn: "একাধিক প্রাপককে দেওয়া যাবে?" }, a: { en: "Yes — Zakat may be distributed among any of the 8 categories listed in Surah At-Tawbah 9:60.", bn: "হ্যাঁ — সূরা আত-তাওবা ৯:৬০ এ উল্লিখিত ৮ শ্রেণির মধ্যে বণ্টন করা যায়।" } },
];

const STORAGE_KEY = "zakat-calc-v1";

export const Route = createFileRoute("/zakat-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "যাকাত ক্যালকুলেটর — Zakat Calculator BD (Vori, BDT)",
      description:
        "যাকাত ক্যালকুলেটর — সোনা (ভরি/গ্রাম), রুপা, নগদ, DPS, FDR, সঞ্চয়পত্র, বিকাশ। হানাফী মাযহাব। Hanafi Zakat calculator for Bangladesh.",
      path: "/zakat-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Zakat Calculator", path: "/zakat-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: ZakatPage,
});

function ZakatPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];

  // Force Bengali default on first visit if user hasn't picked
  useEffect(() => {
    const touched = useAppStore.getState().langTouched;
    if (!touched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [madhhab, setMadhhab] = useState<MadhhabId>("hanafi");
  const [yearType, setYearType] = useState<YearType>("lunar");
  const [nisabBasis, setNisabBasis] = useState<NisabBasis>("silver");
  const [prices, setPrices] = useState<PriceInputs>({
    gold22k: DEFAULT_PRICES.gold22k,
    gold21k: DEFAULT_PRICES.gold21k,
    gold18k: DEFAULT_PRICES.gold18k,
    goldOther: DEFAULT_PRICES.goldTraditional,
    silver: DEFAULT_PRICES.silver,
  });
  const [assets, setAssets] = useState<AssetInputs>(EMPTY_ASSETS);
  const [liabilities, setLiabilities] = useState<LiabilityInputs>(EMPTY_LIABILITIES);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Restore from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data.madhhab) setMadhhab(data.madhhab);
      if (data.yearType) setYearType(data.yearType);
      if (data.nisabBasis) setNisabBasis(data.nisabBasis);
      if (data.prices) setPrices((p) => ({ ...p, ...data.prices }));
      if (data.assets) setAssets((a) => ({ ...a, ...data.assets, gold: { ...a.gold, ...(data.assets.gold ?? {}) } }));
      if (data.liabilities) setLiabilities((l) => ({ ...l, ...data.liabilities }));
      if (data.savedAt) setSavedAt(data.savedAt);
    } catch {
      /* noop */
    }
  }, []);

  const result: ZakatResult = useMemo(
    () => calculateZakat({ madhhab, yearType, nisabBasis, prices, assets, liabilities }),
    [madhhab, yearType, nisabBasis, prices, assets, liabilities],
  );

  const fmt = (v: number) => formatBDT(v, lang, 0);
  const numLoc = (v: number | string) => (lang === "bn" ? toBengaliNumerals(v) : String(v));

  const onSave = () => {
    const stamp = new Date().toISOString();
    const data = { madhhab, yearType, nisabBasis, prices, assets, liabilities, savedAt: stamp };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setSavedAt(stamp);
    } catch {
      /* noop */
    }
  };

  const onReset = () => {
    setMadhhab("hanafi");
    setYearType("lunar");
    setNisabBasis("silver");
    setAssets(EMPTY_ASSETS);
    setLiabilities(EMPTY_LIABILITIES);
    setPrices({
      gold22k: DEFAULT_PRICES.gold22k,
      gold21k: DEFAULT_PRICES.gold21k,
      gold18k: DEFAULT_PRICES.gold18k,
      goldOther: DEFAULT_PRICES.goldTraditional,
      silver: DEFAULT_PRICES.silver,
    });
  };

  const totalGoldVori =
    assets.gold.k22Vori + assets.gold.k21Vori + assets.gold.k18Vori + assets.gold.otherVori;
  const totalSilverVori = assets.silverVori;

  const m = getMadhhab(madhhab);

  return (
    <CalculatorLayout
      slug="zakat-calculator"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="HandCoins"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      <div className={lang === "bn" ? "font-bn space-y-6" : "space-y-6"}>
        {/* Disclaimer */}
        <div className="flex gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>{t(STR.disclaimer)}</p>
        </div>

        {/* Madhhab + Year */}
        <Section title={t(STR.madhhab)}>
          <div className="flex flex-wrap gap-2">
            {MADHHABS.map((mh) => (
              <Chip
                key={mh.id}
                active={madhhab === mh.id}
                onClick={() => setMadhhab(mh.id)}
                label={`${mh.name.bn} / ${mh.name.en}`}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t(m.notes)}</p>
        </Section>

        <div className="grid gap-4 md:grid-cols-2">
          <Section title={t(STR.nisabMethod)}>
            <div className="flex flex-col gap-2">
              <Chip active={nisabBasis === "silver"} onClick={() => setNisabBasis("silver")} label={t(STR.silverNisab)} />
              <Chip active={nisabBasis === "gold"} onClick={() => setNisabBasis("gold")} label={t(STR.goldNisab)} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {nisabBasis === "silver"
                ? `${numLoc(NISAB_SILVER_G)} g ≈ ${numLoc((NISAB_SILVER_G / GRAMS_PER_VORI).toFixed(2))} vori → ${fmt(result.silverNisabBdt)}`
                : `${numLoc(NISAB_GOLD_G)} g ≈ ${numLoc((NISAB_GOLD_G / GRAMS_PER_VORI).toFixed(2))} vori → ${fmt(result.goldNisabBdt)}`}
            </p>
          </Section>

          <Section title={t(STR.yearType)}>
            <div className="flex flex-col gap-2">
              <Chip active={yearType === "lunar"} onClick={() => setYearType("lunar")} label={t(STR.lunar)} />
              <Chip active={yearType === "gregorian"} onClick={() => setYearType("gregorian")} label={t(STR.gregorian)} />
            </div>
          </Section>
        </div>

        {/* Prices */}
        <Section title={t(STR.prices)}>
          <p className="mb-2 text-xs text-muted-foreground">
            {t(STR.pricesNote)} <span className="font-semibold">{SOURCE} · {LAST_UPDATED}</span>
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <PriceField label="22K" value={prices.gold22k} onChange={(v) => setPrices({ ...prices, gold22k: v })} lang={lang} />
            <PriceField label="21K" value={prices.gold21k} onChange={(v) => setPrices({ ...prices, gold21k: v })} lang={lang} />
            <PriceField label="18K" value={prices.gold18k} onChange={(v) => setPrices({ ...prices, gold18k: v })} lang={lang} />
            <PriceField label={lang === "bn" ? "অন্য" : "Other"} value={prices.goldOther} onChange={(v) => setPrices({ ...prices, goldOther: v })} lang={lang} />
            <PriceField label={lang === "bn" ? "রুপা" : "Silver"} value={prices.silver} onChange={(v) => setPrices({ ...prices, silver: v })} lang={lang} />
          </div>
        </Section>

        {/* Assets */}
        <Section title={t(STR.assets)}>
          <Subsection title={t(STR.gold)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <NumField label={lang === "bn" ? "২২ ক্যারেট" : "22K"} value={assets.gold.k22Vori} onChange={(v) => setAssets({ ...assets, gold: { ...assets.gold, k22Vori: v } })} lang={lang} />
              <NumField label={lang === "bn" ? "২১ ক্যারেট" : "21K"} value={assets.gold.k21Vori} onChange={(v) => setAssets({ ...assets, gold: { ...assets.gold, k21Vori: v } })} lang={lang} />
              <NumField label={lang === "bn" ? "১৮ ক্যারেট" : "18K"} value={assets.gold.k18Vori} onChange={(v) => setAssets({ ...assets, gold: { ...assets.gold, k18Vori: v } })} lang={lang} />
              <NumField label={lang === "bn" ? "অন্যান্য" : "Other"} value={assets.gold.otherVori} onChange={(v) => setAssets({ ...assets, gold: { ...assets.gold, otherVori: v } })} lang={lang} />
            </div>
            {totalGoldVori > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {numLoc(totalGoldVori.toFixed(3))} {lang === "bn" ? "ভরি" : "vori"} = {numLoc(voriToGrams(totalGoldVori).toFixed(2))} g · {fmt(result.breakdown.goldValue)}
              </p>
            )}
          </Subsection>

          <Subsection title={t(STR.silver)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <NumField label={lang === "bn" ? "ভরি" : "Vori"} value={assets.silverVori} onChange={(v) => setAssets({ ...assets, silverVori: v })} lang={lang} />
            </div>
            {totalSilverVori > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {numLoc(totalSilverVori.toFixed(3))} {lang === "bn" ? "ভরি" : "vori"} = {numLoc(voriToGrams(totalSilverVori).toFixed(2))} g · {fmt(result.breakdown.silverValue)}
              </p>
            )}
          </Subsection>

          <Subsection title={t(STR.cashBank)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <NumField label={lang === "bn" ? "নগদ" : "Cash in hand"} value={assets.cashInHand} onChange={(v) => setAssets({ ...assets, cashInHand: v })} lang={lang} />
              <NumField label={lang === "bn" ? "সঞ্চয় হিসাব" : "Savings"} value={assets.savings} onChange={(v) => setAssets({ ...assets, savings: v })} lang={lang} />
              <NumField label={lang === "bn" ? "চলতি হিসাব" : "Current"} value={assets.current} onChange={(v) => setAssets({ ...assets, current: v })} lang={lang} />
              <NumField label="FDR" value={assets.fdr} onChange={(v) => setAssets({ ...assets, fdr: v })} lang={lang} />
              <NumField label="DPS" value={assets.dps} onChange={(v) => setAssets({ ...assets, dps: v })} lang={lang} />
              <NumField label={lang === "bn" ? "সঞ্চয়পত্র" : "Sanchaypatra"} value={assets.sanchaypatra} onChange={(v) => setAssets({ ...assets, sanchaypatra: v })} lang={lang} />
            </div>
          </Subsection>

          <Subsection title={t(STR.wallets)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <NumField label="bKash" value={assets.bkash} onChange={(v) => setAssets({ ...assets, bkash: v })} lang={lang} />
              <NumField label="Nagad" value={assets.nagad} onChange={(v) => setAssets({ ...assets, nagad: v })} lang={lang} />
              <NumField label="Rocket" value={assets.rocket} onChange={(v) => setAssets({ ...assets, rocket: v })} lang={lang} />
              <NumField label={lang === "bn" ? "অন্যান্য" : "Other"} value={assets.otherWallet} onChange={(v) => setAssets({ ...assets, otherWallet: v })} lang={lang} />
            </div>
          </Subsection>

          <Subsection title={t(STR.investments)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <NumField label={lang === "bn" ? "শেয়ার" : "Stocks"} value={assets.stocks} onChange={(v) => setAssets({ ...assets, stocks: v })} lang={lang} />
              <NumField label={lang === "bn" ? "বন্ড" : "Bonds"} value={assets.bonds} onChange={(v) => setAssets({ ...assets, bonds: v })} lang={lang} />
              <NumField label={lang === "bn" ? "মিউচুয়াল ফান্ড" : "Mutual funds"} value={assets.mutualFunds} onChange={(v) => setAssets({ ...assets, mutualFunds: v })} lang={lang} />
              <NumField label={lang === "bn" ? "বীমা বোনাস" : "Insurance bonus"} value={assets.insuranceBonus} onChange={(v) => setAssets({ ...assets, insuranceBonus: v })} lang={lang} />
              <NumField label={lang === "bn" ? "ক্রিপ্টো" : "Crypto"} value={assets.crypto} onChange={(v) => setAssets({ ...assets, crypto: v })} lang={lang} />
            </div>
          </Subsection>

          <Subsection title={t(STR.business)}>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <NumField label={lang === "bn" ? "ইনভেন্টরি" : "Inventory"} value={assets.inventory} onChange={(v) => setAssets({ ...assets, inventory: v })} lang={lang} />
              <NumField label={lang === "bn" ? "বকেয়া" : "Receivables"} value={assets.receivables} onChange={(v) => setAssets({ ...assets, receivables: v })} lang={lang} />
              <NumField label={lang === "bn" ? "কাঁচামাল" : "Raw materials"} value={assets.rawMaterials} onChange={(v) => setAssets({ ...assets, rawMaterials: v })} lang={lang} />
            </div>
          </Subsection>

          <Subsection title={t(STR.property)}>
            <p className="mb-2 text-xs text-muted-foreground">
              {lang === "bn"
                ? "বসবাসের নিজস্ব বাড়িতে যাকাত নেই। শুধু পুনঃবিক্রয় বা ভাড়ার জন্য রাখা সম্পত্তি।"
                : "Personal residence is NOT zakatable. Only resale / commercial property here."}
            </p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <NumField label={lang === "bn" ? "সম্পত্তি (পুনঃবিক্রয়)" : "Resale property"} value={assets.resaleProperty} onChange={(v) => setAssets({ ...assets, resaleProperty: v })} lang={lang} />
              <NumField label={lang === "bn" ? "জমি (পুনঃবিক্রয়)" : "Resale land"} value={assets.resaleLand} onChange={(v) => setAssets({ ...assets, resaleLand: v })} lang={lang} />
            </div>
          </Subsection>
        </Section>

        {/* Liabilities */}
        <Section title={t(STR.liabilities)}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <NumField label={lang === "bn" ? "ব্যক্তিগত ঋণ" : "Personal loans"} value={liabilities.personalLoans} onChange={(v) => setLiabilities({ ...liabilities, personalLoans: v })} lang={lang} />
            <NumField label={lang === "bn" ? "ক্রেডিট কার্ড" : "Credit card"} value={liabilities.creditCard} onChange={(v) => setLiabilities({ ...liabilities, creditCard: v })} lang={lang} />
            <NumField label={lang === "bn" ? "বিল বকেয়া" : "Bills due"} value={liabilities.billsDue} onChange={(v) => setLiabilities({ ...liabilities, billsDue: v })} lang={lang} />
            <NumField label={lang === "bn" ? "ব্যবসায়িক ঋণ" : "Business debts"} value={liabilities.businessDebts} onChange={(v) => setLiabilities({ ...liabilities, businessDebts: v })} lang={lang} />
            <NumField label={lang === "bn" ? "মর্টগেজ কিস্তি" : "Mortgage installment"} value={liabilities.mortgageInstallment} onChange={(v) => setLiabilities({ ...liabilities, mortgageInstallment: v })} lang={lang} />
          </div>
        </Section>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={onSave}><Save size={14} className="mr-1" />{t(STR.save)}</Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}><Printer size={14} className="mr-1" />{t(STR.print)}</Button>
          <Button variant="ghost" size="sm" onClick={onReset}><Eraser size={14} className="mr-1" />{t(STR.reset)}</Button>
          {savedAt && (
            <span className="self-center text-xs text-muted-foreground">
              {t(STR.saved)}: {new Date(savedAt).toLocaleString(lang === "bn" ? "bn-BD" : "en-GB")}
            </span>
          )}
        </div>

        {/* Result */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border p-5 ${
            result.obligatory
              ? "border-emerald-400/40 bg-emerald-50 dark:border-emerald-700/50 dark:bg-emerald-950/30"
              : "border-border bg-muted/40"
          }`}
        >
          <div className="flex items-start gap-3">
            <Scale size={22} className={result.obligatory ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"} />
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(STR.status)}</p>
              <p className={`mt-1 text-lg font-semibold ${result.obligatory ? "text-emerald-800 dark:text-emerald-200" : ""}`}>
                {result.obligatory ? t(STR.exceeded) : t(STR.belowNisab)}
              </p>
              {result.obligatory && (
                <p className="mt-3 text-3xl font-bold tabular-nums md:text-4xl">
                  {fmt(result.zakatDue)}
                  <span className="ml-2 text-sm font-medium text-muted-foreground">
                    ({(result.rate * 100).toFixed(result.rate === 0.025 ? 1 : 4)}%)
                  </span>
                </p>
              )}
            </div>
          </div>

          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-border/60">
              <Row label={t(STR.totalAssets)} value={fmt(result.totalAssets)} />
              <Row label={t(STR.totalLiabilities)} value={`(${fmt(result.totalLiabilities)})`} />
              <Row label={t(STR.netWealth)} value={fmt(result.netWealth)} bold />
              <Row label={t(STR.nisab)} value={fmt(result.nisabBdt)} />
              <Row label={t(STR.zakatDue)} value={fmt(result.zakatDue)} bold />
            </tbody>
          </table>

          {/* Visual breakdown */}
          {result.totalAssets > 0 && (
            <div className="mt-4">
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                {(
                  [
                    ["bg-amber-500", result.breakdown.goldValue, lang === "bn" ? "সোনা" : "Gold"],
                    ["bg-slate-400", result.breakdown.silverValue, lang === "bn" ? "রুপা" : "Silver"],
                    ["bg-emerald-500", result.breakdown.cashAndBank, lang === "bn" ? "নগদ" : "Cash"],
                    ["bg-pink-500", result.breakdown.wallets, "Wallets"],
                    ["bg-indigo-500", result.breakdown.investments, lang === "bn" ? "বিনিয়োগ" : "Invest"],
                    ["bg-orange-500", result.breakdown.business, lang === "bn" ? "ব্যবসা" : "Business"],
                    ["bg-cyan-500", result.breakdown.property, lang === "bn" ? "সম্পত্তি" : "Property"],
                  ] as [string, number, string][]
                ).map(([cls, val], i) => {
                  const pct = (val / result.totalAssets) * 100;
                  return pct > 0 ? <div key={i} className={cls} style={{ width: `${pct}%` }} title={`${val}`} /> : null;
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* Recipients */}
        <Section title={lang === "bn" ? "যাকাত যাঁদের দেওয়া যায় (সূরা আত-তাওবা ৯:৬০)" : "Zakat may be given to (Surah At-Tawbah 9:60)"}>
          <ol className="grid gap-2 md:grid-cols-2">
            {RECIPIENTS.map((r, i) => (
              <li key={i} className="rounded-lg border border-border bg-card/60 p-3 text-sm">
                <span className="font-semibold">{numLoc(i + 1)}. {lang === "bn" ? r.bn : r.en}</span>
                <span className="text-muted-foreground"> — {t(r.desc)}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Educational content */}
        <article className="prose prose-sm dark:prose-invert max-w-none space-y-3 text-sm">
          <h2 className="flex items-center gap-2 text-lg font-bold"><BookOpen size={18} /> {lang === "bn" ? "যাকাত গাইড" : "Zakat Guide"}</h2>
          <p>
            {lang === "bn"
              ? "যাকাত ইসলামের পঞ্চম স্তম্ভের একটি (সূরা আল-বাকারা ২:৪৩, ২:২৬৭)। বছরে একবার নিসাব পরিমাণ সম্পদের ২.৫% গরিব ও অভাবগ্রস্তদের দিতে হয়।"
              : "Zakat is one of the five pillars of Islam (Quran 2:43, 2:267). Each year, 2.5% of wealth above Nisab is due to the poor and needy."}
          </p>
          <p>
            <strong>{lang === "bn" ? "নিসাব:" : "Nisab:"}</strong>{" "}
            {lang === "bn"
              ? "৮৭.৪৮ গ্রাম সোনা বা ৬১২.৩৬ গ্রাম রুপার সমতুল্য সম্পদ। হানাফী মতে রুপার নিসাব প্রাধান্য পায় (অধিক অন্তর্ভুক্তিমূলক)।"
              : "Equivalent to 87.48 g gold or 612.36 g silver. Hanafi prefers silver Nisab (more inclusive)."}
          </p>
          <p>
            <strong>{lang === "bn" ? "ভরি ↔ গ্রাম:" : "Vori ↔ gram:"}</strong> 1 vori = {GRAMS_PER_VORI} g · 1 g = {(1 / GRAMS_PER_VORI).toFixed(4)} vori.
          </p>
          <p>
            <strong>{lang === "bn" ? "DPS, FDR, সঞ্চয়পত্র:" : "DPS, FDR, Sanchaypatra:"}</strong>{" "}
            {lang === "bn"
              ? "যাকাতের দিন এসব হিসাবে যত টাকা জমা ও মুনাফা আছে — সবই যাকাতযোগ্য।"
              : "The full balance plus accrued profit on your Zakat date is zakatable."}
          </p>
          <p>
            <strong>{lang === "bn" ? "বিকাশ / নগদ / রকেট:" : "bKash / Nagad / Rocket:"}</strong>{" "}
            {lang === "bn" ? "মোবাইল ওয়ালেটের ব্যালান্স নগদের মতোই গণ্য।" : "Mobile wallet balance is treated the same as cash."}
          </p>
          <p>
            <strong>{lang === "bn" ? "উশর (কৃষি):" : "Ushr (agriculture):"}</strong>{" "}
            {lang === "bn"
              ? "বৃষ্টির পানিতে সেচ হলে ফসলের ১০%, কৃত্রিম সেচে ৫% — যাকাত থেকে আলাদা।"
              : "10% on rain-fed crops, 5% if irrigation cost was incurred. Separate from Zakat on wealth."}
          </p>
          <p>
            <strong>{lang === "bn" ? "পশু:" : "Livestock:"}</strong>{" "}
            {lang === "bn"
              ? "উট ৫টি, গরু ৩০টি, ছাগল ৪০টি — এ সংখ্যা থেকে যাকাত শুরু হয়। সঠিক পরিমাণের জন্য আলেমের পরামর্শ নিন।"
              : "Camels (≥5), cattle (≥30), sheep/goats (≥40). Consult a scholar for exact thresholds."}
          </p>
          <p>
            <strong>{lang === "bn" ? "হাওয়াল:" : "Hawl:"}</strong>{" "}
            {lang === "bn"
              ? "এক চাঁদ্র বছর সম্পদ নিসাবের সমান বা উপরে থাকতে হবে।"
              : "Wealth must remain at or above Nisab for one full lunar year."}
          </p>
          <p className="text-xs text-muted-foreground">
            {lang === "bn"
              ? "বাংলাদেশের কিছু পরিচিত যাকাত সংগঠন: সেন্টার ফর যাকাত ম্যানেজমেন্ট (CZM), সওয়াব ফাউন্ডেশন, অভিযাত্রিক — তথ্যমূলক, কোনো অনুমোদন নয়।"
              : "BD organizations such as Center for Zakat Management (CZM), Sawab, Obhizatrik — informational only, not endorsements."}
          </p>
        </article>
      </div>
    </CalculatorLayout>
  );
}

/* ---------- helpers ---------- */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card/60 p-3">
      <p className="mb-2 text-xs font-semibold">{title}</p>
      {children}
    </div>
  );
}

function Chip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent/50"
      }`}
    >
      {label}
    </button>
  );
}

function NumField({
  label,
  value,
  onChange,
  lang,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  lang: "en" | "bn";
}) {
  const display = value === 0 ? "" : lang === "bn" ? toBengaliNumerals(String(value)) : String(value);
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={display}
        placeholder="0"
        onChange={(e) => {
          const raw = toEnglishNumerals(e.target.value).replace(/,/g, "").trim();
          if (raw === "") return onChange(0);
          const n = Number(raw);
          if (Number.isFinite(n) && n >= 0) onChange(n);
        }}
        className="w-full rounded-md border border-border bg-muted px-2 py-2 text-sm tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-1 focus:ring-accent/30"
      />
    </label>
  );
}

function PriceField(props: { label: string; value: number; onChange: (v: number) => void; lang: "en" | "bn" }) {
  return <NumField {...props} />;
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <tr className={bold ? "font-semibold" : ""}>
      <td className="py-2 text-muted-foreground">{label}</td>
      <td className="py-2 text-right tabular-nums">{value}</td>
    </tr>
  );
}
